import type { GeoJsonDocument, GeoJsonFeature, GeoJsonGeometry, GeoJsonPosition } from "../../core/types/geojson";
import { DEFAULT_MAP_CENTER } from "../../config/map.config";
import { GeoJsonParser } from "./GeoJsonParser";

export type BoundsTuple = [[number, number], [number, number]];

interface FeatureBoundsCandidate {
  bounds: BoundsTuple;
  center: [number, number];
  hasName: boolean;
  distanceToDefaultCenter: number;
}

export class GeoJsonBounds {
  static fromDocument(document: GeoJsonDocument): BoundsTuple | null {
    const collection = GeoJsonParser.normalizeToFeatureCollection(document);
    const points: GeoJsonPosition[] = [];
    collection.features.forEach((feature) => this.collectFeature(feature, points));
    return this.fromPositions(points);
  }

  /**
   * Returns the best viewport bounds for the map after import.
   *
   * The default map view is centered around NE Africa / the Middle East. Some
   * sample files can contain distant regions, and a naive fit to the whole file
   * can zoom out too far. This method fits the whole file when it is
   * geographically coherent, and otherwise focuses the named feature closest to
   * the default map center.
   */
  static preferredViewportFromDocument(document: GeoJsonDocument): BoundsTuple | null {
    const collection = GeoJsonParser.normalizeToFeatureCollection(document);
    const whole = this.fromDocument(collection);
    if (!whole) return null;

    const [west, south] = whole[0];
    const [east, north] = whole[1];
    const width = Math.abs(east - west);
    const height = Math.abs(north - south);

    if (width <= 45 && height <= 25) return whole;

    const candidates = collection.features
      .map((feature) => this.candidateFromFeature(feature))
      .filter((candidate): candidate is FeatureBoundsCandidate => candidate !== null);

    if (!candidates.length) return whole;

    candidates.sort((a, b) => {
      if (a.hasName !== b.hasName) return a.hasName ? -1 : 1;
      return a.distanceToDefaultCenter - b.distanceToDefaultCenter;
    });

    return candidates[0].bounds;
  }


  static viewportNotice(document: GeoJsonDocument): string {
    const collection = GeoJsonParser.normalizeToFeatureCollection(document);
    const whole = this.fromDocument(collection);
    if (!whole) return "";
    const [west, south] = whole[0];
    const [east, north] = whole[1];
    const width = Math.abs(east - west);
    const height = Math.abs(north - south);
    if (width <= 45 && height <= 25) return " Viewport fitted to the full imported file.";
    return " Viewport focused on the named feature closest to the default map center because the file contains distant regions.";
  }

  static fromPositions(positions: GeoJsonPosition[]): BoundsTuple | null {
    if (!positions.length) return null;
    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;

    for (const [lng, lat] of positions) {
      minLng = Math.min(minLng, lng);
      minLat = Math.min(minLat, lat);
      maxLng = Math.max(maxLng, lng);
      maxLat = Math.max(maxLat, lat);
    }

    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
    return [[minLng, minLat], [maxLng, maxLat]];
  }

  private static candidateFromFeature(feature: GeoJsonFeature): FeatureBoundsCandidate | null {
    const points: GeoJsonPosition[] = [];
    this.collectFeature(feature, points);
    const bounds = this.fromPositions(points);
    if (!bounds) return null;
    const center: [number, number] = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];
    return {
      bounds,
      center,
      hasName: typeof feature.properties?.name === "string" && feature.properties.name.trim().length > 0,
      distanceToDefaultCenter: this.distanceSquared(center, DEFAULT_MAP_CENTER)
    };
  }

  private static distanceSquared(a: [number, number], b: [number, number]): number {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    return dx * dx + dy * dy;
  }

  private static collectFeature(feature: GeoJsonFeature, output: GeoJsonPosition[]): void {
    if (feature.geometry) this.collectGeometry(feature.geometry, output);
  }

  private static collectGeometry(geometry: GeoJsonGeometry, output: GeoJsonPosition[]): void {
    switch (geometry.type) {
      case "Point":
        output.push(geometry.coordinates);
        break;
      case "MultiPoint":
      case "LineString":
        output.push(...geometry.coordinates);
        break;
      case "MultiLineString":
      case "Polygon":
        geometry.coordinates.flat().forEach((position) => output.push(position));
        break;
      case "MultiPolygon":
        geometry.coordinates.flat(2).forEach((position) => output.push(position));
        break;
      case "GeometryCollection":
        geometry.geometries.forEach((item) => this.collectGeometry(item, output));
        break;
    }
  }
}
